/**
 * The presenter session: turns presenter commands into beats, the auto-tour,
 * freeze and the demo-data profile, and keeps usePresenter current for the
 * overlay. usePresenterKeys creates one with the real stores and engines;
 * the tests create one with fakes and fake timers.
 *
 * Rules:
 * - A beat key always (re)applies that beat; a beat in progress is cancelled
 *   first, and a frozen picture is unfrozen first.
 * - The tour dwells on a beat once it is shown (or timed out), then moves to
 *   the next one and loops after the last. Manual keys keep the tour running
 *   from the new beat.
 * - Freezing cancels a beat that is still setting up, pauses the tour
 *   countdown and holds the clock, the twin data source and a playing
 *   procedure; unfreezing restores them and resumes the countdown.
 * - Leaving presenter mode stops the tour, unfreezes and turns the demo
 *   profile off; the session being torn down (the shell unmounting) also
 *   stops the tour and unfreezes.
 * - A beat that timed out keeps being checked while it is on screen; its
 *   "not ready" note shrinks as parts arrive and goes once all of them have.
 */
import { BEATS } from './beats';
import {
  applyBeat,
  freeze,
  missingChecks,
  unfreeze,
  type BeatOutcome,
  type ControllerDeps,
} from './controller';
import type { DemoDataController } from './demoData';
import type { PresenterCommand } from './keyboard';
import { usePresenter } from './store';

export interface SessionDeps extends ControllerDeps {
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
  /** Loud, visible report (a toast in the app). */
  notifyError(text: string): void;
  demoData: Pick<DemoDataController, 'setEnabled' | 'dispose'>;
}

export interface PresenterSession {
  run(command: PresenterCommand): void;
  goTo(index: number): Promise<void>;
  next(): void;
  prev(): void;
  toggleTour(): void;
  toggleFreeze(): void;
  toggleDemo(): void;
  /** Called when useUI.presenter changes. */
  presenterChanged(on: boolean): void;
  /** Restores a frozen picture without touching the tour (page unload). */
  releaseFreeze(): void;
  dispose(): void;
}

/** How often a timed-out beat is checked again. */
export const LATE_CHECK_MS = 500;

export function createPresenterSession(deps: SessionDeps): PresenterSession {
  const get = usePresenter.getState;
  const set = usePresenter.setState;
  let current: AbortController | null = null;
  let tourTimer: unknown = null;
  let lateTimer: unknown = null;
  let disposed = false;

  const stopLateCheck = () => {
    if (lateTimer !== null) deps.clearTimer(lateTimer);
    lateTimer = null;
  };

  /**
   * Re-checks a timed-out beat until its parts are all there, updating its note. A note that lists
   * skipped actions stays once nothing is missing: those actions never ran, and waiting will not run them.
   */
  const scheduleLateCheck = (index: number) => {
    stopLateCheck();
    lateTimer = deps.setTimer(() => {
      lateTimer = null;
      const note = get().lastError;
      if (disposed || get().activeIndex !== index || note?.kind !== 'not-ready' || note.beatIndex !== index)
        return;
      const missing = missingChecks(BEATS[index].ready, deps);
      if (missing.length === 0) {
        set({ lastError: note.skipped.length > 0 ? { ...note, missing: [] } : null });
        return;
      }
      if (missing.join('|') !== note.missing.join('|')) set({ lastError: { ...note, missing } });
      scheduleLateCheck(index);
    }, LATE_CHECK_MS);
  };

  const dwellMs = (index: number | null): number | null =>
    index === null ? null : (BEATS[index]?.dwellSeconds ?? 0) * 1000;

  const clearTourTimer = () => {
    if (tourTimer !== null) deps.clearTimer(tourTimer);
    tourTimer = null;
  };

  const scheduleTour = (ms: number, totalMs: number) => {
    clearTourTimer();
    set({ tourDeadline: deps.now() + ms, tourTotalMs: totalMs, tourRemainingMs: null });
    tourTimer = deps.setTimer(() => {
      tourTimer = null;
      set({ tourDeadline: null });
      const index = get().activeIndex;
      start(index === null ? 0 : (index + 1) % BEATS.length);
    }, ms);
  };

  const cancelCurrent = () => {
    current?.abort();
    current = null;
  };

  const releaseFreeze = () => {
    const { frozen, freezeSnapshot } = get();
    if (!frozen) return;
    if (freezeSnapshot) unfreeze(freezeSnapshot, deps);
    set({ frozen: false, freezeSnapshot: null });
  };

  const afterBeat = (index: number) => {
    if (disposed || !get().touring || get().frozen) return;
    const ms = dwellMs(index) ?? 0;
    scheduleTour(ms, ms);
  };

  const goTo = async (index: number): Promise<void> => {
    const beat = BEATS[index];
    if (!beat) throw new Error(`Presenter: no beat at index ${index}`);
    cancelCurrent();
    clearTourTimer();
    stopLateCheck();
    releaseFreeze();
    const ctrl = new AbortController();
    current = ctrl;
    set({ activeIndex: index, applying: true, lastError: null, tourDeadline: null, tourRemainingMs: null });

    let outcome: BeatOutcome;
    try {
      outcome = await applyBeat(beat, deps, { signal: ctrl.signal });
    } catch (err) {
      console.error(`[presenter] beat ${index + 1} (${beat.id}) failed:`, err);
      if (current !== ctrl) return;
      current = null;
      const message = err instanceof Error ? err.message : String(err);
      set({
        applying: false,
        lastError: { beatIndex: index, kind: 'error', message, missing: [], skipped: [] },
      });
      deps.notifyError(`Beat ${index + 1} failed: ${message}`);
      afterBeat(index);
      return;
    }
    if (current !== ctrl || outcome.status === 'cancelled') return;
    current = null;
    if (outcome.status === 'not-ready') {
      const parts = [...outcome.missing, ...outcome.skipped.map((s) => `skipped ${s}`)];
      const message = `Not fully shown after ${Math.round(outcome.elapsedMs / 1000)} s: ${parts.join('; ')}`;
      console.warn(`[presenter] beat ${index + 1} (${beat.id}): ${message}`);
      set({
        applying: false,
        lastError: {
          beatIndex: index,
          kind: 'not-ready',
          message,
          missing: outcome.missing,
          skipped: outcome.skipped,
        },
      });
      if (outcome.missing.length > 0) scheduleLateCheck(index);
    } else {
      set({ applying: false, lastError: null });
    }
    afterBeat(index);
  };

  /** goTo for callers that do not await: anything it throws is a defect and is reported loudly. */
  const start = (index: number) => {
    goTo(index).catch((err: unknown) => {
      console.error('[presenter] goTo failed:', err);
      deps.notifyError(`Presenter error: ${err instanceof Error ? err.message : String(err)}`);
    });
  };

  const next = () => {
    const index = get().activeIndex;
    if (index === null) start(0);
    else if (index < BEATS.length - 1) start(index + 1);
  };

  const prev = () => {
    const index = get().activeIndex;
    if (index !== null && index > 0) start(index - 1);
  };

  const stopTour = () => {
    clearTourTimer();
    set({ touring: false, tourDeadline: null, tourRemainingMs: null, tourTotalMs: null });
  };

  const toggleTour = () => {
    if (get().touring) {
      stopTour();
      return;
    }
    set({ touring: true });
    const index = get().activeIndex;
    if (index === null) {
      start(0);
      return;
    }
    // A beat still setting up schedules its own dwell once it is shown.
    if (get().applying) return;
    const ms = dwellMs(index) ?? 0;
    if (get().frozen) set({ tourRemainingMs: ms, tourTotalMs: ms });
    else scheduleTour(ms, ms);
  };

  const toggleFreeze = () => {
    if (get().frozen) {
      releaseFreeze();
      if (get().touring && !get().applying) {
        const remaining = get().tourRemainingMs ?? dwellMs(get().activeIndex);
        if (remaining !== null) scheduleTour(remaining, get().tourTotalMs ?? remaining);
      }
      return;
    }
    if (current) {
      cancelCurrent();
      set({ applying: false });
    }
    let remaining: number | null = null;
    if (get().touring) {
      const deadline = get().tourDeadline;
      remaining = deadline !== null ? Math.max(0, deadline - deps.now()) : dwellMs(get().activeIndex);
      clearTourTimer();
    }
    const snapshot = freeze(deps);
    set({ frozen: true, freezeSnapshot: snapshot, tourDeadline: null, tourRemainingMs: remaining });
  };

  const toggleDemo = () => {
    const on = !get().demoProfile;
    set({ demoProfile: on });
    deps.demoData.setEnabled(on);
  };

  const presenterChanged = (on: boolean) => {
    if (on) return;
    cancelCurrent();
    stopTour();
    stopLateCheck();
    set({ applying: false, helpOpen: false });
    releaseFreeze();
    // Outside presenter mode there is no DEMO DATA badge and no D key: the profile must not linger unseen.
    if (get().demoProfile) {
      set({ demoProfile: false });
      deps.demoData.setEnabled(false);
    }
  };

  const run = (command: PresenterCommand) => {
    switch (command.type) {
      case 'beat':
        start(command.index);
        return;
      case 'next':
        next();
        return;
      case 'prev':
        prev();
        return;
      case 'tour':
        toggleTour();
        return;
      case 'freeze':
        toggleFreeze();
        return;
      case 'demo':
        toggleDemo();
        return;
      case 'captions':
        set({ captionVisible: !get().captionVisible });
        return;
      case 'help':
        set({ helpOpen: !get().helpOpen });
        return;
      case 'escape':
        if (get().helpOpen) set({ helpOpen: false });
        else if (deps.ui().presenter) deps.ui().togglePresenter();
        return;
    }
  };

  // A session created while the profile is already on (the shell remounted) picks the watch back up.
  if (get().demoProfile) deps.demoData.setEnabled(true);

  return {
    run,
    goTo,
    next,
    prev,
    toggleTour,
    toggleFreeze,
    toggleDemo,
    presenterChanged,
    releaseFreeze,
    dispose() {
      disposed = true;
      cancelCurrent();
      stopTour();
      stopLateCheck();
      // The shell unmounting (Back to /admin) must not strand the clock and the persisted twin source.
      releaseFreeze();
      set({ applying: false });
      deps.demoData.dispose();
    },
  };
}
